import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getTickets, updateTicket, replyToTicket } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { LifeBuoy, Loader2, MessageCircle, Send } from 'lucide-react';

export default function AdminSupportTickets(): React.JSX.Element {
  const toast = useToast();
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await getTickets();
      let list = res.tickets || [];
      if (statusFilter) {
        list = list.filter((t: any) => t.status === statusFilter);
      }
      setTickets(list);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId: number, status: string) => {
    try {
      await updateTicket(ticketId, { status });
      toast.show('Status updated', 'success');
      loadTickets();
    } catch (err: any) {
      toast.show(err.message || 'Failed to update', 'error');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await replyToTicket(selectedTicket.id, replyText);
      setReplyText('');
      toast.show('Reply sent', 'success');
      // Refresh selected ticket
      const res = await getTickets();
      const updated = res.tickets?.find((t: any) => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
    } catch (err: any) {
      toast.show(err.message || 'Failed to send reply', 'error');
    } finally {
      setSending(false);
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-700';
      case 'in_progress': return 'bg-amber-100 text-amber-600';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <LifeBuoy className="w-7 h-7 text-amber-500" />
          <h1 className="text-2xl font-bold text-slate-900">Support Tickets</h1>
        </div>

        <div className="flex gap-2 mb-4">
          {['', 'open', 'in_progress', 'resolved', 'closed'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {s ? s.replace('_', ' ') : 'All'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              {tickets.length === 0 ? (
                <Card className="text-center py-8 text-slate-500">No tickets found.</Card>
              ) : (
                tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      selectedTicket?.id === ticket.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-slate-900 text-sm">{ticket.subject}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{ticket.user_name} · {ticket.user_email}</p>
                    <p className="text-xs text-slate-400 mt-1">{new Date(ticket.created_at).toLocaleDateString()}</p>
                  </button>
                ))
              )}
            </div>

            {selectedTicket && (
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-slate-900">{selectedTicket.subject}</h2>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-slate-700">{selectedTicket.description}</p>
                </div>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    placeholder="Reply to ticket..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleReply()}
                  />
                  <Button size="sm" onClick={handleReply} disabled={sending}>
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
